"use client";

import _toConsumableArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import { useCompositeHover } from "../__chunks/L63MOBXC.js";
import { ComboboxItemCheckedContext, ComboboxItemValueContext, ComboboxListRoleContext, useComboboxScopedContext } from "../__chunks/3LH6HMTR.js";
import { useCompositeItem } from "../__chunks/NSB34BGG.js";
import "../__chunks/SZHY72KI.js";
import "../__chunks/ZHUQ44XA.js";
import "../__chunks/5VQZOHHZ.js";
import "../__chunks/APGXEJ2Q.js";
import "../__chunks/G4PVPWEW.js";
import "../__chunks/COWZSPJ7.js";
import "../__chunks/CMXOY7UW.js";
import "../__chunks/JDEWEW7S.js";
import "../__chunks/Z4WWVOIF.js";
import "../__chunks/SWN3JYXT.js";
import { createElement, createHook, forwardRef, memo } from "../__chunks/7FZLUSKW.js";
import "../__chunks/CTKPPFRS.js";
import { useBooleanEvent, useEvent, useWrapElement } from "../__chunks/HNHAUIIY.js";
import "../__chunks/SK3NAZA3.js";
import { __objRest, __spreadProps, __spreadValues } from "../__chunks/3YLGPPWQ.js";

// src/combobox/combobox-item.tsx
import { isTextField } from "@ariakit/core/utils/dom";
import { isDownloading, isOpeningInNewTab } from "@ariakit/core/utils/events";
import { hasFocus } from "@ariakit/core/utils/focus";
import { invariant } from "@ariakit/core/utils/misc";
import { useCallback, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "div";
function isSelected(storeValue, itemValue) {
  if (itemValue == null) return;
  if (storeValue == null) return false;
  if (Array.isArray(storeValue)) {
    return storeValue.includes(itemValue);
  }
  return storeValue === itemValue;
}
function getItemRole(popupRole) {
  var _a;
  var itemRoleByPopupRole = {
    menu: "menuitem",
    listbox: "option",
    tree: "treeitem"
  };
  var key = popupRole;
  return (_a = itemRoleByPopupRole[key]) != null ? _a : "option";
}
var useComboboxItem = createHook(function useComboboxItem2(_a) {
  var _b = _a,
    store = _b.store,
    value = _b.value,
    hideOnClick = _b.hideOnClick,
    setValueOnClick = _b.setValueOnClick,
    _b$selectValueOnClick = _b.selectValueOnClick,
    selectValueOnClick = _b$selectValueOnClick === void 0 ? true : _b$selectValueOnClick,
    resetValueOnSelect = _b.resetValueOnSelect,
    _b$focusOnHover = _b.focusOnHover,
    focusOnHover = _b$focusOnHover === void 0 ? false : _b$focusOnHover,
    _b$moveOnKeyPress = _b.moveOnKeyPress,
    moveOnKeyPress = _b$moveOnKeyPress === void 0 ? true : _b$moveOnKeyPress,
    getItemProp = _b.getItem,
    props = __objRest(_b, ["store", "value", "hideOnClick", "setValueOnClick", "selectValueOnClick", "resetValueOnSelect", "focusOnHover", "moveOnKeyPress", "getItem"]);
  var _a2;
  var context = useComboboxScopedContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "ComboboxItem must be wrapped in a ComboboxList or ComboboxPopover component.");
  var getItem = useCallback(function (item) {
    var nextItem = __spreadProps(__spreadValues({}, item), {
      value: value
    });
    if (getItemProp) {
      return getItemProp(nextItem);
    }
    return nextItem;
  }, [value, getItemProp]);
  var multiSelectable = store.useState(function (state) {
    return Array.isArray(state.selectedValue);
  });
  var selected = store.useState(function (state) {
    return isSelected(state.selectedValue, value);
  });
  var resetValueOnSelectState = store.useState("resetValueOnSelect");
  setValueOnClick = setValueOnClick != null ? setValueOnClick : !multiSelectable;
  hideOnClick = hideOnClick != null ? hideOnClick : value != null && !multiSelectable;
  var onClickProp = props.onClick;
  var setValueOnClickProp = useBooleanEvent(setValueOnClick);
  var selectValueOnClickProp = useBooleanEvent(selectValueOnClick);
  var resetValueOnSelectProp = useBooleanEvent((_a2 = resetValueOnSelect != null ? resetValueOnSelect : resetValueOnSelectState) != null ? _a2 : multiSelectable);
  var hideOnClickProp = useBooleanEvent(hideOnClick);
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (isDownloading(event)) return;
    if (isOpeningInNewTab(event)) return;
    if (value != null) {
      if (selectValueOnClickProp(event)) {
        if (resetValueOnSelectProp(event)) {
          store == null ? void 0 : store.resetValue();
        }
        store == null ? void 0 : store.setSelectedValue(function (prevValue) {
          if (!Array.isArray(prevValue)) return value;
          if (prevValue.includes(value)) {
            return prevValue.filter(function (v) {
              return v !== value;
            });
          }
          return [].concat(_toConsumableArray(prevValue), [value]);
        });
      }
      if (setValueOnClickProp(event)) {
        store == null ? void 0 : store.setValue(value);
      }
    }
    if (hideOnClickProp(event)) {
      store == null ? void 0 : store.hide();
    }
  });
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    var baseElement = store == null ? void 0 : store.getState().baseElement;
    if (!baseElement) return;
    if (hasFocus(baseElement)) return;
    var printable = event.key.length === 1;
    if (printable || event.key === "Backspace" || event.key === "Delete") {
      queueMicrotask(function () {
        return baseElement.focus();
      });
      if (isTextField(baseElement)) {
        store == null ? void 0 : store.setValue(baseElement.value);
      }
    }
  });
  if (multiSelectable && selected != null) {
    props = __spreadValues({
      "aria-selected": selected
    }, props);
  }
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(ComboboxItemValueContext.Provider, {
      value: value,
      children: /* @__PURE__ */jsx(ComboboxItemCheckedContext.Provider, {
        value: selected != null ? selected : false,
        children: element
      })
    });
  }, [value, selected]);
  var popupRole = useContext(ComboboxListRoleContext);
  props = __spreadProps(__spreadValues({
    role: getItemRole(popupRole),
    children: value
  }, props), {
    onClick: onClick,
    onKeyDown: onKeyDown
  });
  var moveOnKeyPressProp = useBooleanEvent(moveOnKeyPress);
  props = useCompositeItem(__spreadProps(__spreadValues({
    store: store
  }, props), {
    getItem: getItem,
    // Dispatch a custom event on the combobox input when moving to an item
    // with the keyboard so the Combobox component can enable inline
    // autocompletion.
    moveOnKeyPress: function moveOnKeyPress(event) {
      if (!moveOnKeyPressProp(event)) return false;
      var moveEvent = new Event("combobox-item-move");
      var baseElement = store == null ? void 0 : store.getState().baseElement;
      baseElement == null ? void 0 : baseElement.dispatchEvent(moveEvent);
      return true;
    }
  }));
  props = useCompositeHover(__spreadValues({
    store: store,
    focusOnHover: focusOnHover
  }, props));
  return props;
});
var ComboboxItem = memo(forwardRef(function ComboboxItem2(props) {
  var htmlProps = useComboboxItem(props);
  return createElement(TagName, htmlProps);
}));
export { ComboboxItem, useComboboxItem };